import { CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { IMemberTeamHistoryItem } from '../../../Models/CareTeams/MemberTeamHistoryItem.model';
import { apiString as API_URL } from '../../../utils/constants';
import { formatDate } from '../../../utils/dateHelpers';

interface MemberTeamHistoryProps {
    memberId: string;
}

const PRE_TRACKING_MESSAGE = 'Added before history tracking began';

const displayDate = (date: string | null) => (date ? formatDate(date, 'MM/dd/yyyy') : 'Not recorded');

const MemberTeamHistory: React.FC<MemberTeamHistoryProps> = ({ memberId }: MemberTeamHistoryProps) => {
    const [history, setHistory] = useState<IMemberTeamHistoryItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!memberId) return;
        let isCancelled = false;
        const fetchHistory = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get<IMemberTeamHistoryItem[]>(`${API_URL}/CareTeams/getmemberteamhistory`, { params: { memberId } });
                if (!isCancelled) setHistory(response.data ?? []);
            } catch (err) {
                console.error('Error fetching member team history:', err);
                if (!isCancelled) {
                    setHistory([]);
                    setError('Unable to load team history.');
                }
            } finally {
                if (!isCancelled) setLoading(false);
            }
        };
        fetchHistory();
        return () => {
            isCancelled = true;
        };
    }, [memberId]);

    const renderContent = () => {
        if (loading) return <CircularProgress size={20} sx={{ m: 1 }} />;
        if (error) return <Typography color="error" variant="body2">{error}</Typography>;
        if (history.length === 0) return <Typography variant="body2">Not assigned to a team.</Typography>;

        return (
            <Stack spacing={1} divider={<Divider flexItem />}>
                {history.map((item, index) => {
                    const isPreTracking = item.IsCurrent && !item.StartDate;
                    return (
                        <div key={`${item.TeamId}-${item.StartDate ?? index}`}>
                            <Typography variant="body2" fontWeight="bold">
                                {item.TeamName}
                                {item.IsCurrent ? ' (Current)' : ''}
                            </Typography>
                            {isPreTracking ? (
                                <Typography variant="body2">{PRE_TRACKING_MESSAGE}</Typography>
                            ) : item.IsCurrent ? (
                                <>
                                    <Typography variant="body2">Added: {displayDate(item.StartDate)}</Typography>
                                    <Typography variant="body2">Added by: {item.AddedByName ?? 'Not recorded'}</Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2">
                                        {displayDate(item.StartDate)} - {item.EndDate ? displayDate(item.EndDate) : 'Unknown'}
                                    </Typography>
                                    <Typography variant="body2">Added by: {item.AddedByName ?? 'Not recorded'}</Typography>
                                </>
                            )}
                        </div>
                    );
                })}
            </Stack>
        );
    };

    return (
        <Paper variant="outlined" style={{ padding: 8, maxHeight: 400, overflowY: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
                Team History
            </Typography>
            {renderContent()}
        </Paper>
    );
};

export default MemberTeamHistory;
